/**
 * B545 (d) — the buyer attaches at PAY: the order.buyer.set call for a
 * PENDING order (Cart's Continue checked out with no buyer). The idempotency
 * key follows the DETAILS: a double tap replays the same write, a corrected
 * email is a new one (the door re-binds while the order awaits payment).
 */
export interface BuyerDetails {
  readonly name: string;
  readonly email: string;
  readonly phone: string;
}

/** The Cart step's Continue: cart.checkout names nobody (the buyer comes at Pay). */
export const NO_BUYER: BuyerDetails = Object.freeze({ name: "", email: "", phone: "" });

/**
 * B594 (2) — cart.checkout's body: the buyer fields only when typed (B415:
 * required by the storefront in words, optional on the wire) and the shell
 * the checkout happened in — the funnel marks' own surface — so the
 * operator's Analytics can say paid per surface.
 */
export function checkoutBody(
  cartId: string,
  buyer: BuyerDetails,
  surface: "hosted" | "inline" | "list" | "dialog",
): { cartId: string; buyerEmail?: string; buyerName?: string; buyerPhone?: string; surface: "hosted" | "inline" | "list" | "dialog" } {
  const { email, name, phone } = buyer;
  return {
    cartId,
    ...(email !== "" ? { buyerEmail: email } : {}),
    ...(name !== "" ? { buyerName: name } : {}),
    ...(phone !== "" ? { buyerPhone: phone } : {}),
    surface,
  };
}

/** FNV-1a, 32-bit — a short stable digest for the key (not a secret). */
function digest(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

export function buyerSetCall(
  orderId: string,
  buyer: BuyerDetails,
): {
  readonly body: { orderId: string; buyerEmail: string; buyerName?: string; buyerPhone?: string };
  readonly clientKey: string;
} {
  const email = buyer.email.trim();
  const name = buyer.name.trim();
  const phone = buyer.phone.trim();
  return {
    body: {
      orderId,
      buyerEmail: email,
      ...(name !== "" ? { buyerName: name } : {}),
      ...(phone !== "" ? { buyerPhone: phone } : {}),
    },
    clientKey: `buyer-${orderId}-${digest(`${email.toLowerCase()}\u0000${name}\u0000${phone}`)}`.slice(0, 128),
  };
}
